import { Box, Stack } from '@mui/material'

import { COLORS } from 'styles/colors'
import { TypographyHeader14 } from 'ui/typography/TypographyHeader14'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { GroupCowStatus } from '../../../models/IGroup'
import { ButtonUI } from '../../../ui/buttons/ButtonUI'

import Drawer from 'ui/Drawer'
import CustomTextField from '../../shared/textFields/CustomTextField'
import { useGroupEdit } from './hooks/useGroupEdit'
import GroupStatusSelect from './utils/GroupStatusSelect'

type GroupSpecificEditProps = {
	groupId?: any
	isFirstTry?: boolean
	open: boolean
	onClose: () => void
}

const isEmptyOrNegative = (value: unknown) =>
	value === '' ||
	value == null ||
	!Number.isFinite(Number(value)) ||
	Number(value) < 0

const GroupSpecificEdit = ({
	groupId,
	isFirstTry,
	open,
	onClose,
}: GroupSpecificEditProps) => {
	const {
		groupData,
		initialGroupData,
		setObjectValue,
		saveChangesAtServer,
		isSaving,
		cancelEdit,
		userData,
		permissions,
	} = useGroupEdit({ groupId, isFirstTry, onClose })

	const handleClose = () => {
		onClose()
		cancelEdit()
	}

	if (!groupData) return <></>

	const canEditCattleQuantity = Boolean(
		permissions?.can_edit_cattle_quantity_group &&
			!userData?.company?.food_integration,
	)
	const isTitleInvalid = !groupData.title?.trim()
	const isCapacityInvalid = isEmptyOrNegative(groupData.cattles_limit)
	const isCattleQuantityInvalid = isEmptyOrNegative(groupData.cattles_qty)
	const hasCapacityConflict =
		!isCapacityInvalid &&
		!isCattleQuantityInvalid &&
		Number(groupData.cattles_qty) > Number(groupData.cattles_limit)
	const hasChanges = Boolean(
		initialGroupData &&
			(groupData.title !== initialGroupData.title ||
				groupData.status !== initialGroupData.status ||
				Number(groupData.cattles_limit) !==
					Number(initialGroupData.cattles_limit) ||
				(canEditCattleQuantity &&
					Number(groupData.cattles_qty) !==
						Number(initialGroupData.cattles_qty))),
	)
	const isSaveDisabled =
		!hasChanges ||
		isTitleInvalid ||
		!groupData.status ||
		isCapacityInvalid ||
		(canEditCattleQuantity && isCattleQuantityInvalid) ||
		hasCapacityConflict ||
		isSaving

	return (
		<Drawer
			open={open}
			onClose={handleClose}
			title='Редактирование группы'
			width={500}
			anchor='right'
			transitionDuration={300}
		>
			<Box
				sx={{
					pr: isFirstTry ? '0px' : '30px',
					pl: '24px',
					mt: isFirstTry ? '0px' : '24px',
					minHeight: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Stack direction='column' gap='24px' sx={{ flex: 1 }}>
					<TypographyHeader14>Общая информация</TypographyHeader14>

					<CustomTextField
						label='Название группы'
						value={groupData.title}
						width='450px'
						withLabel
						error={isTitleInvalid}
						helperText={isTitleInvalid ? 'Обязательное поле' : ''}
						onChange={e => setObjectValue('title', e.target.value)}
					/>

					<TypographyHeader14>Статус группы</TypographyHeader14>

					<GroupStatusSelect
						value={
							(Object.keys(GroupCowStatus).find(
								key =>
									GroupCowStatus[key as keyof typeof GroupCowStatus] ===
									groupData.status,
							) as keyof typeof GroupCowStatus | '') ?? ''
						}
						width='450px'
						onChange={key =>
							setObjectValue(
								'status',
								GroupCowStatus[key as keyof typeof GroupCowStatus],
							)
						}
					/>

					<Stack gap='16px'>
						<TypographyHeader14>
							Вместимость группы и количество голов
						</TypographyHeader14>
						<TypographyPrimary>
							Максимальное рекомендуемое количество голов в группе:
						</TypographyPrimary>

						<>
							<CustomTextField
								label='Вместимость группы'
								value={groupData.cattles_limit}
								type='number'
								withLabel
								width='212px'
								error={isCapacityInvalid}
								helperText={
									isCapacityInvalid
										? 'Поле не может быть пустым или отрицательным'
										: ''
								}
								onChange={e => {
									const value = e.target.value
									if (value === '' || Number(value) >= 0)
										setObjectValue('cattles_limit', value)
								}}
							/>
							{canEditCattleQuantity && (
								<Stack gap='16px'>
									<TypographyPrimary>
										Текущее количество голов в группе:
									</TypographyPrimary>

									<CustomTextField
										label='Количество голов'
										value={groupData.cattles_qty}
										type='number'
										withLabel
										width='212px'
										error={isCattleQuantityInvalid || hasCapacityConflict}
										helperText={
											isCattleQuantityInvalid
												? 'Поле не может быть пустым или отрицательным'
												: ''
										}
										onChange={e => {
											const value = e.target.value
											if (value === '' || Number(value) >= 0)
												setObjectValue('cattles_qty', value)
										}}
									/>

									{hasCapacityConflict && (
										<TypographyPrimary sx={{ color: COLORS.error, m: 0 }}>
											Количество голов превышает вместимость группы. Это может
											повлиять на комфорт животных и производственные
											показатели.
										</TypographyPrimary>
									)}
								</Stack>
							)}
							{!canEditCattleQuantity && hasCapacityConflict && (
								<TypographyPrimary sx={{ color: COLORS.error, m: 0 }}>
									Вместимость группы не может быть меньше текущего количества
									голов.
								</TypographyPrimary>
							)}
						</>
					</Stack>
				</Stack>

				<Box
					sx={{
						position: 'sticky',
						bottom: 0,
						mt: '24px',
						pt: '16px',
						pb: '24px',
						backgroundColor: COLORS.white,
						zIndex: 1,
					}}
				>
					<ButtonUI
						sx={{
							width: '450px',
							height: '56px',
							fontWeight: 600,
							fontSize: '14px',
							lineHeight: '17px',
						}}
						onClick={saveChangesAtServer}
						disabled={isSaveDisabled}
					>
						Сохранить
					</ButtonUI>
				</Box>
			</Box>
		</Drawer>
	)
}

export default GroupSpecificEdit
